// DEPRECATED: Use SendFormsDialog instead. This file will be removed in a future cleanup.
/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import {
    Dialog, DialogContent, DialogActions,
    Button, TextField, Box, Typography, Avatar,
    InputAdornment, CircularProgress, Autocomplete,
    Chip, alpha, Paper, Collapse, Alert,
    MenuItem, IconButton, Tooltip, Divider,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { useDispatch, useSelector } from 'src/redux/store';
import { getConsentForms } from 'src/redux/slices/consentV2';
import { callApi } from 'src/utils/apiCall';
import useCopyToClipboard from 'src/hooks/use-copy-to-clipboard';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import useResolvedDomain from 'src/hooks/useResolvedDomain';
import { common } from 'src/theme/palette';
import { SYSTEM_FORM_TYPES, getFormTypeConfig } from 'src/pages/superAdmin/ConsentV2/consentConstants';

// Only show forms whose form_type is one of the 7 valid system types
const VALID_FORM_TYPES = SYSTEM_FORM_TYPES.map((t) => t.value);

// ─── Expiry preset options ───────────────────────────────────────────────────
const EXPIRY_PRESETS = [
    { label: '24 hours', hours: 24 },
    { label: '3 days', hours: 72 },
    { label: '7 days', hours: 168 },
    { label: '14 days', hours: 336 },
    { label: '30 days', hours: 720 },
];

// URL construction lives in ``useResolvedDomain`` — see
// src/hooks/useResolvedDomain.js for the single source of truth.

// ─── Compact step summary (for completed steps) ─────────────────────────────
const StepSummary = ({ icon, label, value, onEdit }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.25,
            px: 2,
            borderRadius: '12px',
            bgcolor: alpha(common.mintyZest, 0.04),
            border: '1px solid',
            borderColor: alpha(common.mintyZest, 0.15),
            mb: 1.5,
        }}
    >
        <Box
            sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                bgcolor: alpha(common.mintyZest, 0.1),
                color: common.mintyZest,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <Iconify icon={icon} width={16} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '10px', fontWeight: 600, letterSpacing: '0.5px', display: 'block' }}>
                {label}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '13px' }} noWrap>
                {value}
            </Typography>
        </Box>
        <Iconify icon="eva:checkmark-circle-2-fill" sx={{ width: 18, color: common.mintyZest, flexShrink: 0, mr: 0.5 }} />
        <Tooltip title="Change">
            <IconButton
                size="small"
                onClick={onEdit}
                sx={{ width: 28, height: 28, color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
            >
                <Iconify icon="eva:edit-2-outline" width={14} />
            </IconButton>
        </Tooltip>
    </Box>
);

StepSummary.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onEdit: PropTypes.func.isRequired,
};

// ─── Lead search result row ─────────────────────────────────────────────────
const LeadOption = ({ lead, ...rest }) => {
    const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed';
    const initials = `${(lead.first_name || '?')[0]}${(lead.last_name || '?')[0]}`.toUpperCase();

    return (
        <Box
            component="li"
            {...rest}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.25,
                px: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: alpha(common.mintyZest, 0.04) },
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 'none' },
            }}
        >
            <Avatar
                sx={{
                    width: 32,
                    height: 32,
                    fontSize: '12px',
                    fontWeight: 700,
                    bgcolor: alpha('#078DEE', 0.12),
                    color: '#078DEE',
                }}
            >
                {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, fontSize: '13px' }}>
                    {name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {lead.lead_contact?.email && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '11px' }}>
                            {lead.lead_contact.email}
                        </Typography>
                    )}
                    {lead.lead_contact?.email && lead.lead_contact?.phone && (
                        <Box component="span" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                    )}
                    {lead.lead_contact?.phone && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '11px' }}>
                            {lead.lead_contact.phone}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

LeadOption.propTypes = {
    lead: PropTypes.object.isRequired,
};

// ─── Main Dialog ─────────────────────────────────────────────────────────────
const ConsentV2LinkDialog = ({ open, onClose, practiceId }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useCustomSnackbar();
    const { copy } = useCopyToClipboard();
    const { forms, isLoading: formsLoading } = useSelector((state) => state.consentV2);

    const [selectedLead, setSelectedLead] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedFormId, setSelectedFormId] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    const [devLink, setDevLink] = useState('');
    const [generatingLink, setGeneratingLink] = useState(false);
    const [copied, setCopied] = useState(false);
    const [expiryHours, setExpiryHours] = useState(168);

    // URL building goes through the shared hook now.
    const { buildFormUrl, buildDevUrl } = useResolvedDomain(open ? practiceId : null);

    // Email sending state
    const [consentTemplateId, setConsentTemplateId] = useState(null);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    // Test email state
    const [showTestEmailInput, setShowTestEmailInput] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [sendingTestEmail, setSendingTestEmail] = useState(false);
    const [testEmailSent, setTestEmailSent] = useState(false);
    const [testEmailError, setTestEmailError] = useState('');

    useEffect(() => {
        if (open && practiceId) {
            dispatch(getConsentForms(practiceId, { limit: 100 }));
            // consent_form_config fetch has moved into ``useResolvedDomain``.

            setTemplateLoading(true);
            callApi(`/api/v1/practices/${practiceId}/engagement-template-preferences/`)
                .then((res) => {
                    if (res.status === 200 && res.data?.results) {
                        const consentPref = res.data.results.find((t) => t.type === 'CONSENT_LINK');
                        setConsentTemplateId(consentPref?.template || null);
                    }
                })
                .catch(() => {})
                .finally(() => setTemplateLoading(false));
        }
    }, [open, practiceId]);

    useEffect(() => {
        if (!open) {
            setSelectedLead(null);
            setSearchResults([]);
            setSelectedFormId('');
            setGeneratedLink('');
            setDevLink('');
            setGeneratingLink(false);
            setCopied(false);
            setExpiryHours(168);
            setConsentTemplateId(null);
            setTemplateLoading(false);
            setSendingEmail(false);
            setEmailSent(false);
            setShowTestEmailInput(false);
            setTestEmail('');
            setSendingTestEmail(false);
            setTestEmailSent(false);
            setTestEmailError('');
        }
    }, [open]);

    const LINK_SEARCH_FIELDS = [
        'id', 'first_name', 'last_name', 'lead_contact',
    ].join(',');

    const debouncedSearch = useCallback(
        debounce(async (query) => {
            if (!query || query.length < 2 || !practiceId) {
                setSearchResults([]);
                setSearchLoading(false);
                return;
            }
            setSearchLoading(true);
            try {
                const response = await callApi(
                    `/api/v1/practices/${practiceId}/leads/search/?query=${encodeURIComponent(query)}&limit=10&offset=0&ordering=-created_at&is_archived=false&fields={${LINK_SEARCH_FIELDS}}`
                );
                if ((response.ok || response.status === 200) && response.data?.results) {
                    setSearchResults(response.data.results);
                } else {
                    setSearchResults([]);
                }
            } catch {
                setSearchResults([]);
            }
            setSearchLoading(false);
        }, 300),
        [practiceId]
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    // ─── Generate link ────────────────────────────────────────────────────
    const handleGenerateLink = async (channel = 'COPY') => {
        if (!selectedFormId || !selectedLead?.id || !practiceId) return;
        setGeneratingLink(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/`,
                'post',
                {
                    form: Number(selectedFormId),
                    lead: selectedLead.id,
                    expires_in_hours: expiryHours,
                    channel,
                }
            );
            if (res.status === 201 || res.status === 200) {
                const token = res.data.access_token;
                const url = buildFormUrl(token);
                setGeneratedLink(url);
                const localUrl = buildDevUrl(token);
                setDevLink(localUrl && localUrl !== url ? localUrl : '');
            } else {
                const errorMsg = typeof res.data?.detail === 'string' ? res.data.detail : 'Failed to generate consent link';
                enqueueSnackbar(errorMsg, { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Failed to generate consent link', { variant: 'error' });
        }
        setGeneratingLink(false);
    };

    const handleFormSelect = (formId) => {
        setSelectedFormId(formId);
        setCopied(false);
        setEmailSent(false);
        setTestEmailSent(false);
        setShowTestEmailInput(false);
        setGeneratedLink('');
    };

    const handleLeadSelect = (_, lead) => {
        setSelectedLead(lead);
        setGeneratedLink('');
        setSelectedFormId('');
        setCopied(false);
        setEmailSent(false);
    };

    const handleCopyLink = async () => {
        if (!generatedLink) return;
        const success = await copy(generatedLink);
        if (success) {
            setCopied(true);
            enqueueSnackbar('Link copied!', { variant: 'success' });
            setTimeout(() => setCopied(false), 2000);
        } else {
            enqueueSnackbar('Failed to copy', { variant: 'error' });
        }
    };

    // ─── Send Email ───────────────────────────────────────────────────────
    const handleSendEmail = async () => {
        if (!generatedLink || !selectedLead || !consentTemplateId) return;
        setSendingEmail(true);
        try {
            const templateRes = await callApi(
                `/api/v1/practices/${practiceId}/engagement-templates/${consentTemplateId}/`
            );
            if (templateRes.status !== 200 || !templateRes.data) {
                enqueueSnackbar('Failed to load consent email template', { variant: 'error' });
                setSendingEmail(false);
                return;
            }
            const template = templateRes.data;
            const replaceMergeTag = (text) =>
                (text || '').replace(/\$consent_form_dynamic_url/g, generatedLink);

            const sendRes = await callApi(
                `/api/v1/practices/${practiceId}/engagement-templates/${consentTemplateId}/send/`,
                'post',
                {
                    lead: selectedLead.id,
                    body: replaceMergeTag(template.body || ''),
                    subject: replaceMergeTag(template.subject || 'Consent Form'),
                    channel: 'EMAIL',
                    category: 'COMMUNICATION',
                }
            );
            if (sendRes.status === 200 || sendRes.status === 201) {
                setEmailSent(true);
                enqueueSnackbar('Consent email sent!', { variant: 'success' });
            } else {
                enqueueSnackbar(typeof sendRes.data === 'string' ? sendRes.data : 'Failed to send email', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Failed to send email', { variant: 'error' });
        }
        setSendingEmail(false);
    };

    // ─── Send Test Email ──────────────────────────────────────────────────
    const handleSendTestEmail = async () => {
        if (!testEmail || !consentTemplateId) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
            setTestEmailError('Please enter a valid email address');
            return;
        }
        setTestEmailError('');
        setSendingTestEmail(true);
        try {
            const sendRes = await callApi(
                `/api/v1/practices/${practiceId}/engagement-templates/${consentTemplateId}/send_test/`,
                'post',
                { email: testEmail }
            );
            if (sendRes.status === 200 || sendRes.status === 201) {
                setTestEmailSent(true);
                enqueueSnackbar('Test email sent!', { variant: 'success' });
                setTimeout(() => setTestEmailSent(false), 3000);
            } else {
                enqueueSnackbar(typeof sendRes.data === 'string' ? sendRes.data : 'Failed to send test email', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Failed to send test email', { variant: 'error' });
        }
        setSendingTestEmail(false);
    };

    const handleSendWhatsApp = () => {
        if (!generatedLink || !selectedLead?.lead_contact?.phone) return;
        const selectedForm = forms.results?.find((f) => String(f.id) === String(selectedFormId));
        const formName = selectedForm?.name || 'Consent Form';
        const phone = selectedLead.lead_contact.phone.replace(/[^0-9+]/g, '');
        const text = encodeURIComponent(
            `Hi ${selectedLead.first_name || ''},\n\nPlease complete your ${formName} by clicking this link:\n${generatedLink}`
        );
        window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
    };

    const getLeadLabel = (lead) => {
        if (!lead) return '';
        const name = `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
        const contact = lead.lead_contact?.email || lead.lead_contact?.phone || '';
        return contact ? `${name} — ${contact}` : name;
    };

    // ─── Step logic ───────────────────────────────────────────────────────
    const getStep = () => {
        if (!selectedLead) return 0;
        if (!generatedLink) return 1;
        return 2;
    };
    const currentStep = getStep();

    const hasEmail = Boolean(selectedLead?.lead_contact?.email);
    const hasPhone = Boolean(selectedLead?.lead_contact?.phone);
    const hasTemplate = Boolean(consentTemplateId);
    const canGenerate = Boolean(selectedFormId && selectedLead?.id);
    const leadName = selectedLead ? `${selectedLead.first_name || ''} ${selectedLead.last_name || ''}`.trim() : '';
    const selectedForm = forms.results?.find((f) => String(f.id) === String(selectedFormId));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
        >
            {/* ─── Header ──────────────────────────────────────────────────── */}
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40, height: 40, borderRadius: '12px',
                                bgcolor: alpha(common.mintyZest, 0.08), color: common.mintyZest,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <Iconify icon="solar:link-round-bold-duotone" width={22} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px', lineHeight: 1.3 }}>
                                Send Consent Form
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                                Generate a personalized link for your patient
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: 'text.disabled' }}>
                        <Iconify icon="eva:close-fill" width={20} />
                    </IconButton>
                </Box>

                {/* Step progress bar */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {[0, 1, 2].map((idx) => (
                        <Box
                            key={idx}
                            sx={{
                                flex: 1, height: 3, borderRadius: '2px',
                                bgcolor: idx <= currentStep ? common.mintyZest : alpha('#919EAB', 0.16),
                                transition: 'background-color 0.3s ease',
                            }}
                        />
                    ))}
                </Box>
            </Box>

            <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
                {/* ─── Step 1: Patient Search ─────────────────────────────── */}
                {currentStep === 0 ? (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '13px' }}>
                            Find patient
                        </Typography>
                        <Autocomplete
                            options={searchResults}
                            getOptionLabel={getLeadLabel}
                            filterOptions={(x) => x}
                            isOptionEqualToValue={(option, val) => option.id === val?.id}
                            value={selectedLead}
                            onChange={handleLeadSelect}
                            onInputChange={(_, inputVal, reason) => {
                                if (reason === 'input') {
                                    if (inputVal.length >= 2) debouncedSearch(inputVal);
                                    else { setSearchResults([]); setSearchLoading(false); }
                                }
                                if (reason === 'clear') {
                                    setSearchResults([]);
                                    setSelectedLead(null);
                                    setSelectedFormId('');
                                    setGeneratedLink('');
                                }
                            }}
                            loading={searchLoading}
                            loadingText={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                    <CircularProgress size={16} sx={{ color: common.mintyZest }} />
                                    <Typography variant="body2" color="text.secondary">Searching...</Typography>
                                </Box>
                            }
                            noOptionsText={
                                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                    Type at least 2 characters to search...
                                </Typography>
                            }
                            renderOption={(props, option) => (
                                <LeadOption key={option.id} lead={option} {...props} />
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by name, email, or phone..."
                                    autoFocus
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20 }} />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {searchLoading ? <CircularProgress size={18} sx={{ color: common.mintyZest }} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            bgcolor: '#f8fafb',
                                            '&.Mui-focused': { bgcolor: 'background.paper' },
                                        },
                                    }}
                                />
                            )}
                            slotProps={{
                                paper: {
                                    sx: { borderRadius: '12px', boxShadow: '0 12px 24px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider', mt: 0.5 },
                                },
                            }}
                        />
                    </Box>
                ) : (
                    /* Collapsed patient summary */
                    <StepSummary
                        icon="eva:person-fill"
                        label="PATIENT"
                        value={`${leadName}${hasEmail ? ` · ${selectedLead.lead_contact.email}` : ''}`}
                        onEdit={() => {
                            setSelectedLead(null);
                            setSelectedFormId('');
                            setGeneratedLink('');
                        }}
                    />
                )}

                {/* ─── Step 2: Form Selection ─────────────────────────────── */}
                {currentStep === 1 && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '13px' }}>
                            Choose consent form
                        </Typography>
                        {(() => {
                            // Filter to valid form types only (excludes call scripts, TCOs, etc.)
                            const validForms = (forms.results || []).filter(
                                (f) => VALID_FORM_TYPES.includes(f.form_type)
                            );
                            if (formsLoading) {
                                return (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                        <CircularProgress size={24} sx={{ color: common.mintyZest }} />
                                    </Box>
                                );
                            }
                            if (validForms.length === 0) {
                                return (
                                    <Alert severity="info" sx={{ borderRadius: '10px' }}>
                                        No forms available. Create one first.
                                    </Alert>
                                );
                            }
                            return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {validForms.map((form) => {
                                    const isSelected = String(selectedFormId) === String(form.id);
                                    const typeConfig = getFormTypeConfig(form.form_type);
                                    let desc = '';
                                    try { desc = JSON.parse(form.description || '{}')?.user_description || ''; } catch { /* */ }
                                    return (
                                        <Paper
                                            key={form.id}
                                            elevation={0}
                                            onClick={() => handleFormSelect(form.id)}
                                            sx={{
                                                p: 1.5, borderRadius: '10px',
                                                border: '1.5px solid',
                                                borderColor: isSelected ? common.mintyZest : 'divider',
                                                bgcolor: isSelected ? alpha(common.mintyZest, 0.03) : 'transparent',
                                                cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                transition: 'all 0.15s ease',
                                                '&:hover': { borderColor: isSelected ? common.mintyZest : 'text.secondary' },
                                            }}
                                        >
                                            <Box sx={{
                                                width: 20, height: 20, borderRadius: '50%',
                                                border: '2px solid', borderColor: isSelected ? common.mintyZest : 'text.disabled',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                                {isSelected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: common.mintyZest }} />}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '13px' }}>
                                                        {form.name}
                                                    </Typography>
                                                    {typeConfig && (
                                                        <Chip
                                                            label={typeConfig.label}
                                                            size="small"
                                                            sx={{
                                                                height: 18, fontSize: '9px', fontWeight: 700,
                                                                bgcolor: alpha(typeConfig.color || '#637381', 0.08),
                                                                color: typeConfig.color || '#637381',
                                                                borderRadius: '5px',
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                {desc && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }} noWrap>
                                                        {desc}
                                                    </Typography>
                                                )}
                                                {form.treatments?.length > 0 && (
                                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                                        {form.treatments.slice(0, 2).map((t, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                label={t.treatment_item?.name || t.name || String(t)}
                                                                size="small"
                                                                sx={{ height: 18, fontSize: '10px', bgcolor: alpha('#078DEE', 0.08), color: '#078DEE', borderRadius: '5px' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Box>
                            );
                        })()}

                        {/* Expiry + Generate */}
                        <Collapse in={canGenerate}>
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
                                    <Iconify icon="eva:clock-outline" sx={{ width: 18, color: 'text.disabled' }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Link expires in
                                    </Typography>
                                    <TextField
                                        select
                                        size="small"
                                        value={expiryHours}
                                        onChange={(e) => setExpiryHours(Number(e.target.value))}
                                        sx={{
                                            minWidth: 130,
                                            '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px' },
                                            '& .MuiSelect-select': { py: 0.75 },
                                        }}
                                    >
                                        {EXPIRY_PRESETS.map((p) => (
                                            <MenuItem key={p.hours} value={p.hours}>{p.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={
                                        generatingLink
                                            ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                                            : <Iconify icon="solar:link-round-bold-duotone" width={18} />
                                    }
                                    onClick={() => handleGenerateLink('COPY')}
                                    disabled={generatingLink}
                                    sx={{
                                        borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                                        py: 1.25, bgcolor: common.mintyZest, boxShadow: 'none',
                                        '&:hover': { bgcolor: '#009c4e', boxShadow: `0 8px 16px ${alpha(common.mintyZest, 0.24)}` },
                                    }}
                                >
                                    {generatingLink ? 'Generating...' : 'Generate Link'}
                                </Button>
                            </Box>
                        </Collapse>
                    </Box>
                )}

                {/* Collapsed form summary (on step 3) */}
                {currentStep === 2 && selectedForm && (
                    <StepSummary
                        icon="solar:document-text-bold"
                        label="CONSENT FORM"
                        value={selectedForm.name}
                        onEdit={() => {
                            setGeneratedLink('');
                            setDevLink('');
                            setCopied(false);
                            setEmailSent(false);
                        }}
                    />
                )}

                {/* ─── Step 3: Share Link ──────────────────────────────────── */}
                {currentStep === 2 && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '13px' }}>
                            Share with patient
                        </Typography>

                        {/* Link display + copy */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2, borderRadius: '12px',
                                bgcolor: alpha(common.mintyZest, 0.04),
                                border: '1px solid', borderColor: alpha(common.mintyZest, 0.2),
                                mb: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Iconify icon="eva:checkmark-circle-2-fill" sx={{ width: 18, color: common.mintyZest }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: common.mintyZest, fontSize: '11px', letterSpacing: '0.3px' }}>
                                    LINK READY
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    p: 1, borderRadius: '8px', bgcolor: '#fff',
                                    border: '1px solid', borderColor: 'divider',
                                }}
                            >
                                <Iconify icon="eva:link-2-fill" sx={{ width: 16, color: 'text.disabled', flexShrink: 0 }} />
                                <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{ flex: 1, fontSize: '12px', color: 'text.secondary', fontFamily: 'monospace' }}
                                >
                                    {generatedLink}
                                </Typography>
                                <Button
                                    size="small"
                                    variant={copied ? 'contained' : 'outlined'}
                                    startIcon={<Iconify icon={copied ? 'eva:checkmark-fill' : 'eva:copy-outline'} width={14} />}
                                    onClick={handleCopyLink}
                                    sx={{
                                        flexShrink: 0, borderRadius: '8px', textTransform: 'none',
                                        fontWeight: 600, fontSize: '11px', px: 1.5, py: 0.5, minWidth: 72,
                                        ...(copied
                                            ? { bgcolor: common.mintyZest, '&:hover': { bgcolor: '#009c4e' } }
                                            : { borderColor: common.mintyZest, color: common.mintyZest }),
                                    }}
                                >
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                            </Box>
                        </Paper>

                        {/* Dev-only localhost link */}
                        {devLink && (
                            <Paper
                                elevation={0}
                                sx={{ p: 1.25, borderRadius: '8px', bgcolor: '#fffbe6', border: '1px dashed #ffab00', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Iconify icon="eva:code-fill" sx={{ width: 16, color: '#B76E00', flexShrink: 0 }} />
                                <Typography
                                    component="a" href={devLink} target="_blank" rel="noopener noreferrer"
                                    variant="caption" noWrap
                                    sx={{ fontSize: '11px', color: '#B76E00', fontFamily: 'monospace', textDecoration: 'underline', flex: 1 }}
                                >
                                    {devLink}
                                </Typography>
                            </Paper>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {/* Send actions */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            {/* Send via Email */}
                            <Button
                                fullWidth
                                variant={emailSent ? 'contained' : 'outlined'}
                                startIcon={
                                    sendingEmail
                                        ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                        : <Iconify icon={emailSent ? 'eva:checkmark-circle-2-fill' : 'eva:email-outline'} width={18} />
                                }
                                onClick={handleSendEmail}
                                disabled={!hasEmail || !hasTemplate || sendingEmail || emailSent || templateLoading}
                                sx={{
                                    borderRadius: '10px', textTransform: 'none', fontWeight: 600, py: 1.25,
                                    ...(emailSent
                                        ? {
                                            bgcolor: common.mintyZest, color: '#fff',
                                            '&:hover': { bgcolor: '#009c4e' },
                                            '&.Mui-disabled': { bgcolor: common.mintyZest, color: '#fff', opacity: 0.9 },
                                        }
                                        : {
                                            borderColor: 'divider', color: 'text.primary',
                                            '&:hover': { borderColor: '#078DEE', bgcolor: alpha('#078DEE', 0.04) },
                                        }),
                                }}
                            >
                                {emailSent ? 'Sent!' : sendingEmail ? 'Sending...' : 'Email'}
                            </Button>

                            {/* WhatsApp */}
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<Iconify icon="ic:baseline-whatsapp" width={18} />}
                                onClick={handleSendWhatsApp}
                                disabled={!hasPhone}
                                sx={{
                                    borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                                    borderColor: 'divider', color: 'text.primary', py: 1.25,
                                    '&:hover': { borderColor: '#25D366', bgcolor: alpha('#25D366', 0.04) },
                                }}
                            >
                                WhatsApp
                            </Button>
                        </Box>

                        {/* No template warning */}
                        {!templateLoading && !hasTemplate && (
                            <Alert
                                severity="warning"
                                variant="outlined"
                                sx={{ mb: 1.5, borderRadius: '10px', fontSize: '11px', '& .MuiAlert-message': { fontSize: '11px' } }}
                            >
                                No consent email template configured. Set one in Message Templates to enable email sending.
                            </Alert>
                        )}

                        {/* Test email */}
                        {hasTemplate && (
                            <Box>
                                {!showTestEmailInput ? (
                                    <Button
                                        size="small"
                                        startIcon={<Iconify icon="eva:paper-plane-outline" width={14} />}
                                        onClick={() => setShowTestEmailInput(true)}
                                        sx={{
                                            textTransform: 'none', fontWeight: 600, color: 'text.secondary',
                                            fontSize: '12px', '&:hover': { bgcolor: alpha('#078DEE', 0.04), color: '#078DEE' },
                                        }}
                                    >
                                        Send test email
                                    </Button>
                                ) : (
                                    <Collapse in={showTestEmailInput}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.5, borderRadius: '10px',
                                                bgcolor: alpha('#078DEE', 0.02),
                                                border: '1px solid', borderColor: alpha('#078DEE', 0.15),
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                <TextField
                                                    size="small"
                                                    placeholder="Enter test email"
                                                    value={testEmail}
                                                    onChange={(e) => { setTestEmail(e.target.value); setTestEmailError(''); }}
                                                    error={Boolean(testEmailError)}
                                                    helperText={testEmailError}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Iconify icon="eva:email-outline" sx={{ width: 16, color: 'text.disabled' }} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        flex: 1,
                                                        '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff', fontSize: '13px' },
                                                    }}
                                                />
                                                <Button
                                                    variant={testEmailSent ? 'contained' : 'outlined'}
                                                    size="small"
                                                    startIcon={
                                                        sendingTestEmail
                                                            ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                                            : <Iconify icon={testEmailSent ? 'eva:checkmark-fill' : 'iconamoon:send-fill'} width={14} />
                                                    }
                                                    onClick={handleSendTestEmail}
                                                    disabled={!testEmail || sendingTestEmail}
                                                    sx={{
                                                        borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                                                        fontSize: '12px', px: 2, height: 40, whiteSpace: 'nowrap',
                                                        ...(testEmailSent
                                                            ? { bgcolor: common.mintyZest, '&:hover': { bgcolor: '#009c4e' }, '&.Mui-disabled': { bgcolor: common.mintyZest, color: '#fff', opacity: 0.9 } }
                                                            : { borderColor: '#078DEE', color: '#078DEE' }),
                                                    }}
                                                >
                                                    {testEmailSent ? 'Sent!' : 'Send'}
                                                </Button>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { setShowTestEmailInput(false); setTestEmail(''); setTestEmailError(''); }}
                                                    sx={{ width: 32, height: 40, color: 'text.disabled' }}
                                                >
                                                    <Iconify icon="eva:close-fill" width={16} />
                                                </IconButton>
                                            </Box>
                                        </Paper>
                                    </Collapse>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                <Button
                    onClick={onClose}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                >
                    {currentStep === 2 ? 'Done' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ConsentV2LinkDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ConsentV2LinkDialog.defaultProps = {
    practiceId: '',
};

export default ConsentV2LinkDialog;
